import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import './Admin.css';
import { PackageIcon, TagIcon, StarIcon } from '../../components/SvgIcons';

const ProductEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const { t, isRTL } = useLanguage();
  const isNew = id === 'new';

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    nameAr: '',
    description: '',
    descriptionAr: '',
    price: '',
    salePrice: '',
    onSale: false,
    image: '',
    category: '',
    categoryAr: '',
    stock: '',
    featured: false,
    hideImage: false,
    enabled: true
  });

  useEffect(() => {
    if (!isNew) {
      fetchProduct();
    }
  }, [id]);

  const fetchProduct = async () => {
    try {
      const response = await fetch(`/api/products/${id}`);
      if (response.ok) {
        const product = await response.json();
        setFormData({
          name: product.name || '',
          nameAr: product.nameAr || '',
          description: product.description || '',
          descriptionAr: product.descriptionAr || '',
          price: product.price?.toString() || '',
          salePrice: product.salePrice?.toString() || '',
          onSale: product.onSale || false,
          image: product.image || '',
          category: product.category || '',
          categoryAr: product.categoryAr || '',
          stock: product.stock?.toString() || '',
          featured: product.featured || false,
          hideImage: product.hideImage || false,
          enabled: product.enabled !== false
        });
      }
    } catch (error) {
      console.error('Failed to fetch product:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    const url = isNew ? '/api/products' : `/api/products/${id}`;
    const method = isNew ? 'POST' : 'PUT';

    try {
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        navigate('/dashboard4/products');
      }
    } catch (error) {
      console.error('Failed to save product:', error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-page">
        <div className="container">
          <div className="loading"><div className="spinner"></div></div>
        </div>
      </div>
    );
  }

  return (
    <div className={`admin-page ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="container">
        <div className="admin-card" style={{ padding: '12px 16px', marginBottom: '24px', ...(window.innerWidth >= 768 ? { position: 'sticky', top: '72px', zIndex: 99 } : {}) }}>
          <div className="admin-nav" style={{ marginBottom: 0 }}>
            <Link to="/dashboard4">{t('dashboard')}</Link>
            <Link to="/dashboard4/products" className="active">{t('products')}</Link>
            <Link to="/dashboard4/orders">{t('orders')}</Link>
            <Link to="/dashboard4/create-order">{isRTL ? 'إنشاء طلب' : 'Create Order'}</Link>
            <Link to="/dashboard4/inventory">{isRTL ? "المخزون" : "Inventory"}</Link>
            <Link to="/dashboard4/stats">{isRTL ? "الإحصائيات" : "Stats"}</Link>
            <Link to="/dashboard4/settings">{isRTL ? 'الإعدادات' : 'Settings'}</Link>
          </div>
        </div>

        <div className="admin-card" style={{ padding: '12px 16px', marginBottom: '20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Link to="/dashboard4/products" style={{ color: '#666', fontSize: '14px', textDecoration: 'none', fontWeight: '600' }}>
            {isRTL ? '→ العودة للمنتجات' : '← Back to Products'}
          </Link>
          {!isNew && (
            <span style={{ fontSize: '13px', color: '#6b7280' }}>
              ID: <span style={{ fontFamily: 'monospace', fontWeight: '600' }}>{id}</span>
            </span>
          )}
        </div>

        <form onSubmit={handleSubmit}>
          <div className="product-edit-lang-grid">

            {/* Left Column - English */}
            <div className="admin-card">
              <h3 style={{ fontSize: '14px', fontWeight: '800', marginBottom: '20px', color: '#484747', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 21V14M4 10V3M12 21V12M12 8V3M20 21V16M20 12V3M1 14h6M9 8h6M17 16h6"/></svg>
                English
              </h3>

              <div className="form-group" style={{ marginBottom: '16px' }}>
                <label className="form-label">{isRTL ? 'اسم المنتج' : 'Product Name'}</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="form-input"
                  required
                />
              </div>

              <div className="form-group" style={{ marginBottom: '16px' }}>
                <label className="form-label">{isRTL ? 'الوصف' : 'Description'}</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  className="form-input"
                  rows="4"
                />
              </div>

              <div className="form-group" style={{ marginBottom: '16px' }}>
                <label className="form-label">{isRTL ? 'الفئة' : 'Category'}</label>
                <input
                  type="text"
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  className="form-input"
                  required
                />
              </div>
            </div>

            {/* Right Column - Arabic */}
            <div className="admin-card">
              <h3 style={{ fontSize: '14px', fontWeight: '800', marginBottom: '20px', color: '#484747', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 21V14M4 10V3M12 21V12M12 8V3M20 21V16M20 12V3M1 14h6M9 8h6M17 16h6"/></svg>
                {isRTL ? 'عربي' : 'Arabic'}
              </h3>

              <div className="form-group" style={{ marginBottom: '16px' }}>
                <label className="form-label">{isRTL ? 'اسم المنتج' : 'Product Name'}</label>
                <input
                  type="text"
                  name="nameAr"
                  value={formData.nameAr}
                  onChange={handleInputChange}
                  className="form-input"
                  dir="rtl"
                  placeholder="الاسم بالعربي"
                />
              </div>

              <div className="form-group" style={{ marginBottom: '16px' }}>
                <label className="form-label">{isRTL ? 'الوصف' : 'Description'}</label>
                <textarea
                  name="descriptionAr"
                  value={formData.descriptionAr}
                  onChange={handleInputChange}
                  className="form-input"
                  rows="4"
                  dir="rtl"
                  placeholder="الوصف بالعربي"
                />
              </div>

              <div className="form-group" style={{ marginBottom: '16px' }}>
                <label className="form-label">{isRTL ? 'الفئة' : 'Category'}</label>
                <input
                  type="text"
                  name="categoryAr"
                  value={formData.categoryAr}
                  onChange={handleInputChange}
                  className="form-input"
                  dir="rtl"
                  placeholder="الفئة بالعربي"
                />
              </div>
            </div>
          </div>

          {/* Product Details */}
          <div className="admin-card" style={{ marginTop: '24px' }}>
            <h3 style={{ fontSize: '14px', fontWeight: '800', marginBottom: '20px', color: '#484747', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <PackageIcon size="18px" />
              {isRTL ? 'تفاصيل المنتج' : 'Product Details'}
            </h3>

            <div className="product-edit-price-stock-grid">
              <div className="form-group">
                <label className="form-label">{t('price')} (AED)</label>
                <input
                  type="number"
                  inputMode="decimal"
                  name="price"
                  value={formData.price}
                  onChange={handleInputChange}
                  className="form-input"
                  step="0.01"
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">{t('stock')}</label>
                <input
                  type="number"
                  inputMode="numeric"
                  name="stock"
                  value={formData.stock}
                  onChange={handleInputChange}
                  className="form-input"
                  required
                />
              </div>
            </div>

            {/* Sale Price - full width */}
            <div style={{ marginTop: '16px', padding: '14px 16px', background: formData.onSale ? '#fef3c7' : '#f9fafb', borderRadius: '10px', border: formData.onSale ? '1px solid #f59e0b' : '1px solid #e5e7eb' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', cursor: 'pointer', fontWeight: '600' }}>
                <input
                  type="checkbox"
                  name="onSale"
                  checked={formData.onSale}
                  onChange={handleInputChange}
                  style={{ width: '16px', height: '16px' }}
                />
                {isRTL ? 'تخفيض' : 'Sale'}
              </label>
              {formData.onSale && (
                <div style={{ marginTop: '10px' }}>
                  <input
                    type="number"
                    inputMode="decimal"
                    name="salePrice"
                    value={formData.salePrice}
                    onChange={handleInputChange}
                    className="form-input"
                    step="0.01"
                    placeholder={isRTL ? 'سعر التخفيض' : 'Sale price (AED)'}
                  />
                  {formData.price && formData.salePrice && parseFloat(formData.salePrice) < parseFloat(formData.price) && (
                    <p style={{ fontSize: '12px', color: '#059669', marginTop: '4px', fontWeight: '600' }}>
                      {isRTL ? `توفير ${(((parseFloat(formData.price) - parseFloat(formData.salePrice)) / parseFloat(formData.price)) * 100).toFixed(0)}%` : `${(((parseFloat(formData.price) - parseFloat(formData.salePrice)) / parseFloat(formData.price)) * 100).toFixed(0)}% off`}
                    </p>
                  )}
                </div>
              )}
            </div>

            <div className="form-group" style={{ marginTop: '16px' }}>
              <label className="form-label">{t('imageUrl')}</label>
              <input
                type="url"
                name="image"
                value={formData.image}
                onChange={handleInputChange}
                className="form-input"
              />
            </div>

            {formData.image && (
              <div style={{ marginTop: '12px' }}>
                <img
                  src={formData.image}
                  alt="Preview"
                  style={{ width: '120px', height: '120px', objectFit: 'cover', borderRadius: '12px', border: '1px solid #eee' }}
                />
              </div>
            )}

            <div className="product-edit-checkboxes">
              <label>
                <input
                  type="checkbox"
                  name="featured"
                  checked={formData.featured}
                  onChange={handleInputChange}
                />
                {isRTL ? 'منتج مميز' : 'Featured Product'}
              </label>

              <label>
                <input
                  type="checkbox"
                  name="hideImage"
                  checked={formData.hideImage}
                  onChange={handleInputChange}
                />
                {isRTL ? 'إخفاء الصورة' : 'Hide Image'}
              </label>
            </div>

            {/* Hidden Product Toggle */}
            <div style={{
              marginTop: '16px',
              padding: '14px 16px',
              background: !formData.enabled ? '#fef2f2' : '#f0fdf4',
              borderRadius: '10px',
              border: !formData.enabled ? '1px solid #fecaca' : '1px solid #bbf7d0'
            }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  name="enabled"
                  checked={!formData.enabled}
                  onChange={(e) => setFormData(prev => ({ ...prev, enabled: !e.target.checked }))}
                  style={{ width: '18px', height: '18px', accentColor: '#dc2626' }}
                />
                <div>
                  <span style={{ fontWeight: '600', fontSize: '14px', color: !formData.enabled ? '#dc2626' : '#374151' }}>
                    {isRTL ? 'إخفاء المنتج' : 'Hide Product'}
                  </span>
                  <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#6b7280' }}>
                    {isRTL
                      ? 'المنتج لن يظهر في المتجر أو صفحة إنشاء الطلبات'
                      : 'Product won\'t appear in the store or create order page'}
                  </p>
                </div>
              </label>
            </div>
          </div>

          {/* Actions */}
          <div className="product-edit-actions">
            <Link
              to="/dashboard4/products"
              className="btn btn-secondary"
              style={{ padding: '12px 28px', fontSize: '15px' }}
            >
              {t('cancel')}
            </Link>
            <button
              type="submit"
              className="btn btn-primary"
              style={{ padding: '12px 28px', fontSize: '15px' }}
              disabled={saving}
            >
              {saving
                ? (isRTL ? 'جاري الحفظ...' : 'Saving...')
                : isNew
                  ? (isRTL ? 'إضافة المنتج' : 'Add Product')
                  : (isRTL ? 'حفظ التعديلات' : 'Save Changes')
              }
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProductEdit;
